import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Copy, FileDown, FileType, Link2, FileSpreadsheet, Loader2, Maximize2, X } from 'lucide-react';
import { AudienceLevel, Cohort, LEVEL_LABELS } from '../../types/pathway';
import { formatHours, totalHours } from '../../utils/pathwayMatching';
import { PathwayButton } from './PathwayButton';
import { PathwayDocument, pageCount } from './PathwayDocument';
import { DocumentViewer } from './DocumentViewer';
import { PrintSurface, printPathwayDocument } from './PrintSurface';
import { buildPathwayCsv, csvFileName, downloadCsv } from '../../utils/pathwayCsv';
import { buildPathwayWord, downloadWord, wordFileName } from '../../utils/pathwayWord';
interface ShareModalProps {
  open: boolean;
  pathwayName: string;
  /** Set in the pathway panel; editable here as a last check before sending. */
  company: string;
  items: string[];
  audience: AudienceLevel | 'any';
  cohort: Cohort | null;
  onCompanyChange: (company: string) => void;
  onClose: () => void;
}
const CORE_FIELDS = ['Description', 'Relevance', 'What you’ll learn'];
const EXTRA_FIELDS = ['Category', 'Duration', 'Topics', 'Skill Module ID'];
type FormatId = 'pdf' | 'link' | 'word' | 'csv';
const FORMATS: {
  id: FormatId;
  label: string;
  blurb: string;
  action: string;
  icon: typeof FileDown;
}[] = [{
  id: 'link',
  label: 'Shareable link',
  blurb: 'Send it and see when they open it',
  action: 'Create Shareable Link',
  icon: Link2
}, {
  id: 'csv',
  label: 'CSV',
  blurb: 'Import file for their L&D team',
  action: 'Download CSV',
  icon: FileSpreadsheet
}, {
  id: 'word',
  label: 'Word',
  blurb: 'Editable, for your own proposal',
  action: 'Download Word',
  icon: FileType
}, {
  id: 'pdf',
  label: 'PDF',
  blurb: 'Branded leave-behind for the prospect',
  action: 'Save as PDF',
  icon: FileDown
}];
export function ShareModal({
  open,
  pathwayName,
  company,
  items,
  audience,
  cohort,
  onCompanyChange,
  onClose
}: ShareModalProps) {
  const [extras, setExtras] = useState<string[]>([]);
  const [showExtras, setShowExtras] = useState(false);
  const [format, setFormat] = useState<FormatId>('pdf');
  const [status, setStatus] = useState<'idle' | 'working' | 'linked'>('idle');
  const [copied, setCopied] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  if (!open) return null;
  const pageEstimate = pageCount(items.length);
  // Keep the "included" list in the dialog's own order, not click order.
  const selectedExtras = EXTRA_FIELDS.filter(field => extras.includes(field));
  const link = `https://phoenix.edu/pd/pathway/${(pathwayName || 'pathway').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-8f2c`;
  const toggleExtra = (field: string) => setExtras(prev => prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]);
  const activeFormat = FORMATS.find(f => f.id === format) ?? FORMATS[0];
  const ActiveIcon = activeFormat.icon;
  const createLink = () => {
    setStatus('working');
    window.setTimeout(() => setStatus('linked'), 700);
  };
  const runFormat = () => {
    if (format === 'pdf') return printPathwayDocument(pathwayName);
    if (format === 'word') return handleDownloadWord();
    if (format === 'csv') return handleDownloadCsv();
    return createLink();
  };
  const copyLink = () => {
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  const handleDownloadWord = () => {
    downloadWord(wordFileName(pathwayName), buildPathwayWord({
      pathwayName,
      company,
      cohort,
      audience,
      items,
      fields: selectedExtras
    }));
  };
  const handleDownloadCsv = () => {
    downloadCsv(csvFileName(pathwayName), buildPathwayCsv({
      pathwayName,
      company,
      cohort,
      audience,
      items,
      fields: selectedExtras
    }));
  };
  return <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4" role="dialog" aria-modal="true" aria-labelledby="share-title" data-id="element-1883" data-mp="14gavs2hiysgz">
      <motion.div initial={{
        opacity: 0,
        y: 12,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-lg" data-id="element-1884" data-mp="uppxyh9odrz4">
        <div className="flex items-start justify-between border-b border-gray-200 p-6" data-id="element-1885" data-mp="7qk2u7ovtdda">
          <div data-id="element-1886" data-mp="bqu63clahpog">
            <h2 id="share-title" className="text-xl font-bold text-gray-900" data-id="element-1887" data-mp="bsf1t5jcbenw">
              Review and send
            </h2>
            <p className="mt-1 text-sm text-gray-600" data-id="element-1888" data-mp="224b86v06hb2x">
              {items.length} modules · {formatHours(totalHours(items))} ·{' '}
              {audience === 'any' ? 'Mixed audience' : LEVEL_LABELS[audience]}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900" data-id="element-1889" data-mp="3mm3xfxmhgyzv">
            <X size={18} data-id="element-1890" data-mp="3vmaajie9rwyi" />
          </button>
        </div>

        <div className="grid flex-1 gap-6 overflow-y-auto p-6 lg:grid-cols-2" data-id="element-1891" data-mp="3vkpflro1mc04">
          <div className="space-y-5" data-id="element-1892" data-mp="e853t2mzhppj">
            <label className="block" data-id="element-1893" data-mp="97h29n14pqh2">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1894" data-mp="96fqfgcagzya">
                Company
              </span>
              <input value={company} onChange={e => onCompanyChange(e.target.value)} placeholder="Meridian Health" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1895" data-mp="i7vp4qtqrdmo" />
              <span className="mt-1 block text-xs text-gray-500" data-id="element-1896" data-mp="nxoybt6ngu85">
                Appears on the cover of the PDF and the shared page.
              </span>
            </label>

            <div data-id="element-1897" data-mp="xsz3gpschhq5">
              <span className="block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1898" data-mp="xsgy93npft63">
                Included for every module
              </span>
              <ul className="mt-2 space-y-1.5" data-id="element-1899" data-mp="16vi1mwl6r662">
                {[...CORE_FIELDS, ...selectedExtras].map(field => <li key={field} className="flex items-center gap-2 text-sm text-gray-700" data-id="element-1900" data-mp="1bsi6j46au2vl">
                    <span className="flex h-4 w-4 items-center justify-center rounded bg-[#007558]" data-id="element-1901" data-mp="1ljmirw4lb9mq">
                      <Check size={11} className="text-white" data-id="element-1902" data-mp="1plhl93y9bt23" />
                    </span>
                    {field}
                  </li>)}
              </ul>
              <p className="mt-2 text-xs text-gray-500" data-id="element-1903" data-mp="1ui7xfg6xy4i8">
                The first three answer what a prospect actually asks. Anything
                you add here appears in every format you download.
              </p>

              <button type="button" onClick={() => setShowExtras(!showExtras)} aria-expanded={showExtras} className="mt-3 flex items-center gap-1 text-sm font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-1904" data-mp="361dksa89efbg">
                More fields
                <ChevronDown size={14} className={`transition-transform duration-150 ${showExtras ? 'rotate-180' : ''}`} data-id="element-1905" data-mp="3tsamku6cbw8z" />
              </button>

              <AnimatePresence initial={false} data-id="element-1906" data-mp="3jzbz5fmkybqy">
                {showExtras && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.18,
                  ease: [0.23, 1, 0.32, 1]
                }} className="overflow-hidden" data-id="element-1907" data-mp="lcza3y4wy3ae">
                    <div className="mt-3 space-y-2" data-id="element-1908" data-mp="3b4mme5vnx2my">
                      {EXTRA_FIELDS.map(field => <label key={field} className="flex items-center gap-2 text-sm text-gray-700" data-id="element-1909" data-mp="3g2wicip7o79m">
                          <input type="checkbox" checked={extras.includes(field)} onChange={() => toggleExtra(field)} className="h-4 w-4 rounded border-gray-300 text-[#DB3725] focus:ring-[#30718D]" data-id="element-1910" data-mp="cmpb627l7rw3" />
                          {field}
                        </label>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50 p-5" data-id="element-1911" data-mp="2ftelqj7baxz4">
            <div className="flex items-center justify-between" data-id="element-1912" data-mp="2av4tagcz2bh7">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1913" data-mp="2pt5g8kvvhcz7">
                Document preview
              </span>
              <button type="button" onClick={() => setViewerOpen(true)} className="flex items-center gap-1 text-sm font-medium text-[#DB3725] transition-colors hover:text-[#c02f1f]" data-id="element-1914" data-mp="2kuvjw6yew6c0">
                <Maximize2 size={14} data-id="element-1915" data-mp="26ub2nf85aikm" />
                View full document
              </button>
            </div>

            <button type="button" onClick={() => setViewerOpen(true)} aria-label="Open full document preview" className="mt-3 flex w-full justify-center rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1916" data-mp="223b5c8v45yhl">
              <div className="h-[412px] w-[318px] overflow-hidden rounded-lg border border-gray-200 bg-white" data-id="element-1917" data-mp="17dqep8c61he1">
                <div className="w-[816px] origin-top-left scale-[0.39]" data-id="element-1918" data-mp="13c56w1ehntap">
                  <PathwayDocument pathwayName={pathwayName} company={company} cohort={cohort} audience={audience} items={items} fields={selectedExtras} coverOnly data-id="element-1919" data-mp="ydbpvyzztk5s" />
                </div>
              </div>
            </button>

            <p className="mt-3 text-xs text-gray-500" data-id="element-1920" data-mp="jml00whyov5b">
              {pageEstimate} {pageEstimate === 1 ? 'page' : 'pages'} · cover
              plus the modules in order.
            </p>
          </div>
        </div>

        <div className="border-t border-gray-200 p-6" data-id="element-1921" data-mp="5omw6l19oh42">
          <AnimatePresence initial={false} data-id="element-1922" data-mp="5o4wtfjb5mrx">
            {status === 'linked' && <motion.div initial={{
              opacity: 0,
              y: -6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.2,
              ease: [0.23, 1, 0.32, 1]
            }} className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3" data-id="element-1923" data-mp="qux9727fuqd">
                <Link2 size={16} className="text-[#30718D]" data-id="element-1924" data-mp="hpy31jp1uwy" />
                <code className="flex-1 truncate text-sm text-gray-700" data-id="element-1925" data-mp="5hufctsfeto8">
                  {link}
                </code>
                <PathwayButton variant="secondary" size="sm" onClick={copyLink} data-id="element-1926" data-mp="3422rj3u7ussd">
                  {copied ? <Check size={15} data-id="element-1927" data-mp="390dwb05nmd3q" /> : <Copy size={15} data-id="element-1928" data-mp="38xi13jl6y2iz" />}
                  {copied ? 'Copied' : 'Copy'}
                </PathwayButton>
                <span className="w-full text-xs text-gray-500" data-id="element-1929" data-mp="3dwulzysi4zix">
                  Anyone with the link can view it. You’ll see when the prospect
                  opens it.
                </span>
              </motion.div>}
          </AnimatePresence>

          <fieldset data-id="element-1930" data-mp="2q7ju76t4qfgx">
            <legend className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1931" data-mp="2q70bhnvnnn1u">
              Choose a format
            </legend>
            <div className="grid gap-3 sm:grid-cols-4" data-id="element-1932" data-mp="2da3v4d8qsy17">
              {FORMATS.map(option => {
                const Icon = option.icon;
                const active = format === option.id;
                return <label key={option.id} className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${active ? 'border-[#30718D] bg-[#30718D]/5' : 'border-gray-200 bg-white hover:bg-gray-50'}`} data-id="element-1933" data-mp="2n0gcjwl9gb34">
                    <input type="radio" name="pathway-format" value={option.id} checked={active} onChange={() => setFormat(option.id)} className="mt-0.5 h-4 w-4 border-gray-300 text-[#30718D] focus:ring-[#30718D]" data-id="element-1934" data-mp="248e4h9moq34h" />
                    <span className="min-w-0" data-id="element-1935" data-mp="3s6ew4mymhsp4">
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900" data-id="element-1936" data-mp="3s77w9532tjya">
                        <Icon size={14} aria-hidden="true" data-id="element-1937" data-mp="3e7yp0uwum06g" />
                        {option.label}
                      </span>
                      <span className="mt-0.5 block text-xs text-gray-500" data-id="element-1938" data-mp="3iabmkw1fyrs5">
                        {option.blurb}
                      </span>
                    </span>
                  </label>;
              })}
            </div>
          </fieldset>

          <div className="mt-5 flex flex-wrap items-center justify-end gap-3" data-id="element-1939" data-mp="zc5bwmp5off">
            <PathwayButton variant="secondary" onClick={onClose} data-id="element-1940" data-mp="50xfha7ujolx">
              Cancel
            </PathwayButton>
            <PathwayButton onClick={runFormat} disabled={status === 'working'} className="min-w-[220px]" data-id="element-1941" data-mp="sq8gmst3zl0l">
              {status === 'working' ? <Loader2 size={18} className="animate-spin" data-id="element-1942" data-mp="35zvmnqzxajyo" /> : <ActiveIcon size={18} data-id="element-1943" data-mp="30zq1mto5rvpk" />}
              {format === 'link' && status === 'linked' ? 'Regenerate Link' : activeFormat.action}
            </PathwayButton>
          </div>
        </div>
      </motion.div>
    </div>

    <DocumentViewer open={viewerOpen} pathwayName={pathwayName} company={company} cohort={cohort} audience={audience} items={items} fields={selectedExtras} onClose={() => setViewerOpen(false)} onDownloadWord={handleDownloadWord} onDownloadCsv={handleDownloadCsv} data-id="element-1944" data-mp="3opkdb7wqre88" />

    <PrintSurface pathwayName={pathwayName} company={company} cohort={cohort} audience={audience} items={items} fields={selectedExtras} data-id="element-1945" data-mp="3ayyrpbfwkq6n" />
    </>;
}